/**
 * useVirtualBackground — MediaPipe SelfieSegmentation-powered virtual background.
 *
 * Given a source MediaStream (raw camera) + a background preset
 * ({ id, type: "none" | "blur" | "image", src? }), returns a NEW MediaStream
 * where the person is composited onto the selected background in real-time.
 *
 * Returns { outputStream, ready } — outputStream is `null` when the effect is
 * off (preset.type === "none"), in which case callers should pass the raw
 * camera stream to peers directly.
 *
 * Uses an offscreen <canvas> + captureStream() to avoid touching React DOM.
 */
import { useEffect, useRef, useState } from "react";

const MP_CDN = "https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/";

// Loads the MediaPipe UMD bundle from the CDN as a <script> tag and reads the
// global it defines. Used as a fallback when the npm package isn't installed
// or the dynamic import otherwise fails (e.g. blocked bundler resolution).
let _cdnScriptPromise = null;
function loadMediaPipeFromCDN() {
  if (window.SelfieSegmentation) return Promise.resolve(window.SelfieSegmentation);
  if (!_cdnScriptPromise) {
    _cdnScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = `${MP_CDN}selfie_segmentation.js`;
      s.async = true;
      s.onload = () => resolve(window.SelfieSegmentation || null);
      s.onerror = () => reject(new Error("CDN script failed to load"));
      document.head.appendChild(s);
    });
  }
  return _cdnScriptPromise;
}

// Lazy-load mediapipe so a failed dynamic import can never crash the meeting UI.
// Falls back to loading the UMD build straight from the CDN when the npm
// import fails (missing package, bundler issue, etc).
async function loadMediaPipe() {
  try {
    const mod = await import("@mediapipe/selfie_segmentation");
    const found = mod?.SelfieSegmentation || mod?.default?.SelfieSegmentation || null;
    if (found) return found;
    throw new Error("module loaded but SelfieSegmentation export missing");
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[VirtualBackground] npm import failed, falling back to CDN:", e?.message);
    try {
      const cdn = await loadMediaPipeFromCDN();
      if (cdn) return cdn;
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.warn("[VirtualBackground] CDN fallback also failed, effects disabled:", e2?.message);
    }
    return null;
  }
}

export function useVirtualBackground(sourceStream, preset) {
  const canvasRef = useRef(null);
  const ctxRef    = useRef(null);
  const videoRef  = useRef(null);      // hidden <video> playing the raw camera stream
  const segRef    = useRef(null);      // SelfieSegmentation instance
  const bgImgRef  = useRef(null);      // preloaded HTMLImageElement for image bg
  const outStreamRef = useRef(null);
  const runningRef = useRef(false);
  const rafRef    = useRef(0);
  const [outputStream, setOutputStream] = useState(null);
  const [ready, setReady] = useState(false);

  // onResults is registered ONCE on the SelfieSegmentation instance (it's
  // expensive to re-create), so it must never read `preset`/`cancelled`
  // directly from the effect's closure — those go stale the moment the user
  // picks a different background or the effect re-runs. Read through refs
  // instead so every frame uses the CURRENT preset and active flag.
  const presetRef = useRef(preset);
  const activeRef = useRef(false);
  useEffect(() => { presetRef.current = preset; }, [preset]);

  // Video tracks get swapped in place (e.g. camera re-acquired after a
  // screen-share ends) without the MediaStream object's identity changing,
  // so the effect also needs to re-run when the underlying track id changes.
  const videoTrackId = sourceStream?.getVideoTracks?.()[0]?.id || null;

  // Preload background image whenever preset changes
  useEffect(() => {
    if (preset?.type === "image" && preset.src) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => { bgImgRef.current = img; };
      img.src = preset.src;
    } else {
      bgImgRef.current = null;
    }
  }, [preset?.id, preset?.src, preset?.type]);

  useEffect(() => {
    // No source or effect turned off → tear down and expose nothing
    if (!sourceStream || !preset || preset.type === "none") {
      cleanup();
      setOutputStream(null);
      setReady(false);
      return;
    }

    let cancelled = false;
    activeRef.current = true;

    (async () => {
      try {
        // 1. Hidden <video> element playing the raw camera stream — MUST be
        //    appended to the DOM (even off-screen) or browsers won't actually
        //    render frames, which would make the output canvas stay black.
        if (!videoRef.current) {
          const v = document.createElement("video");
          v.autoplay = true; v.playsInline = true; v.muted = true;
          v.setAttribute("playsinline", "true");
          v.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:2px;height:2px;opacity:0;pointer-events:none;";
          document.body.appendChild(v);
          videoRef.current = v;
        }
        videoRef.current.srcObject = sourceStream;
        try { await videoRef.current.play(); } catch { /* noop */ }
        // Wait until the video actually has frames — otherwise the first
        // segmentation calls fail and the output stream is a black canvas.
        await new Promise((resolve) => {
          const v = videoRef.current;
          if (v.readyState >= 2 && v.videoWidth > 0) return resolve();
          const onReady = () => { v.removeEventListener("loadeddata", onReady); resolve(); };
          v.addEventListener("loadeddata", onReady);
          setTimeout(resolve, 1500);
        });

        // 2. Offscreen canvas — capped at 640×360 for smooth 24fps segmentation
        //    on typical laptop CPUs. Meeting tiles are never larger than this
        //    in practice, and going higher freezes the composited video.
        const targetW = Math.min(videoRef.current.videoWidth || 640, 640);
        const targetH = Math.min(videoRef.current.videoHeight || 360, 360);
        if (!canvasRef.current) {
          const c = document.createElement("canvas");
          c.width = targetW;
          c.height = targetH;
          canvasRef.current = c;
          ctxRef.current = c.getContext("2d");
        } else {
          canvasRef.current.width = targetW;
          canvasRef.current.height = targetH;
        }

        // 3. SelfieSegmentation model — loaded from CDN (WASM/tflite assets)
        if (!segRef.current) {
          const SelfieSegmentation = await loadMediaPipe();
          if (!SelfieSegmentation) {
            // Effects unavailable — bail out silently, meeting still works without them.
            setOutputStream(null);
            setReady(false);
            return;
          }
          const seg = new SelfieSegmentation({ locateFile: (f) => MP_CDN + f });
          seg.setOptions({ modelSelection: 1, selfieMode: false });
          seg.onResults((results) => {
            if (!activeRef.current) return;
            drawFrame(results, presetRef.current, canvasRef.current, ctxRef.current, bgImgRef.current);
          });
          await seg.initialize();
          segRef.current = seg;
        }

        // 4. captureStream() from canvas → this is what peers will receive.
        // We capture at 24fps to match the segmentation loop, avoiding extra
        // work sending duplicate frames when the composited canvas hasn't
        // changed yet.
        if (!outStreamRef.current) {
          const cs = canvasRef.current.captureStream(24);
          // Mix in the original audio tracks so mic still works
          sourceStream.getAudioTracks().forEach(t => cs.addTrack(t.clone()));
          outStreamRef.current = cs;
        }
        setOutputStream(outStreamRef.current);
        setReady(true);

        // 5. Continuous send-frame → segmentation loop.
        // We throttle to ~24fps and only start a NEW send when the previous
        // one has fully returned. This prevents mediapipe queue buildup that
        // freezes the composited video when the CPU can't keep up.
        runningRef.current = true;
        let inFlight = false;
        const targetFps = 24;
        const minDelta = 1000 / targetFps;
        let lastTick = 0;
        const loop = async (now) => {
          if (!runningRef.current || cancelled) return;
          if (!inFlight && now - lastTick >= minDelta) {
            const v = videoRef.current;
            if (v && v.readyState >= 2 && v.videoWidth > 0) {
              inFlight = true;
              lastTick = now;
              try { await segRef.current.send({ image: v }); } catch { /* noop */ }
              inFlight = false;
            }
          }
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      } catch (e) {
        // Fallback silently — caller will just use the raw stream
        // eslint-disable-next-line no-console
        console.warn("[VirtualBackground] init failed:", e?.message || e);
        setOutputStream(null);
      }
    })();

    return () => { cancelled = true; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceStream, preset?.id, preset?.type, videoTrackId]);

  function cleanup() {
    activeRef.current = false;
    runningRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    if (outStreamRef.current) {
      outStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } });
      outStreamRef.current = null;
    }
  }

  return { outputStream, ready };
}

function drawFrame(results, preset, canvas, ctx, bgImg) {
  if (!ctx || !canvas || !results?.image) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.save();
  ctx.clearRect(0, 0, w, h);

  // 1. Draw the raw camera frame
  ctx.drawImage(results.image, 0, 0, w, h);

  // 2. Keep only the person pixels — destination-in with mask (white = person)
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(results.segmentationMask, 0, 0, w, h);

  // 3. Draw the virtual background UNDERNEATH the person cutout
  ctx.globalCompositeOperation = "destination-over";
  if (preset.type === "blur") {
    const blurPx = typeof preset.intensity === "number" ? preset.intensity : 18;
    ctx.filter = `blur(${blurPx}px)`;
    ctx.drawImage(results.image, 0, 0, w, h);
    ctx.filter = "none";
  } else if (preset.type === "image" && bgImg) {
    const iw = bgImg.width, ih = bgImg.height;
    const scale = Math.max(w / iw, h / ih);
    const dw = iw * scale, dh = ih * scale;
    ctx.drawImage(bgImg, (w - dw) / 2, (h - dh) / 2, dw, dh);
  } else {
    ctx.fillStyle = "#0b1220";
    ctx.fillRect(0, 0, w, h);
  }

  ctx.globalCompositeOperation = "source-over";
  ctx.restore();
}
